<script setup lang="ts">
import AnimatedLogoLoader from '@/components/createdUi/AnimatedLogoLoader.vue'
import EmployeeSelectionModal from '@/components/createdUi/EmployeeSelectionModal.vue'
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'
import { getApiError } from '@/composables/getApiError'
import { useApiStore } from '@/modules/apiStore/stores/apiStore'
import type { IEmployeeTreeItem } from '@/modules/apiStore/types/employees/IEmployeeTree'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { usePermissionsStore } from '@/modules/permissions/stores/permissionsStore'
import { refDebounced } from '@vueuse/core'
import EditEmployeeDialog from '../EditEmployeeDialog.vue'
import { useStaffSettingsStore } from '../../stores/staffSettingsStore'
import { useStaffStore } from '../../stores/staffStore'
import PositionFormModal from './PositionFormModal.vue'
import StaffSettingsEmployeePanel from './StaffSettingsEmployeePanel.vue'
import StaffSettingsEntityRow from './StaffSettingsEntityRow.vue'
import StaffSettingsSplit from './StaffSettingsSplit.vue'
import TransferEmployeesModal from './TransferEmployeesModal.vue'

const staffSettingsStore = useStaffSettingsStore()
const staffStore = useStaffStore()
const apiStore = useApiStore()
const feedbackStore = useFeedbackStore()

const permissionsStore = usePermissionsStore()
const canEditEmployees = computed(() =>
  !!permissionsStore.modules.employee_management?.employee_edit,
)
const editEmployeeOpen = ref(false)
const editEmployeeId = ref<string | null>(null)

function openEditEmployee(id: string) {
  editEmployeeId.value = id
  editEmployeeOpen.value = true
}

const dragEnabled = ref(false)
const createOpen = ref(false)
const editOpen = ref(false)
const transferOpen = ref(false)
const transferring = ref(false)
const deleteOpen = ref(false)
const blockedOpen = ref(false)
const editingPositionId = ref<string | null>(null)
const deleteName = ref('')
const deleteId = ref<string | null>(null)
const blockedCount = ref(0)

const selectedId = ref<string | null>(null)
const dragOverId = ref<string | null>(null)
const selectedEmployeeIds = ref<string[]>([])

const listSearch = ref('')
const listSearchDebounced = refDebounced(listSearch, 300)
const listSearchLoading = ref(false)
let listSearchSeq = 0

const employeeModalRef = ref<InstanceType<typeof EmployeeSelectionModal> | null>(null)

const employeeHeaders = [
  { title: 'ФИО', key: 'fio', sortable: true },
  { title: 'Подразделение', key: 'department_name', sortable: true, filterType: 'tree', filterKey: 'departments' },
  { title: 'Телефон', key: 'phone_number', sortable: true },
  { title: 'Email', key: 'email', sortable: true },
]

const selectedEmployees = computed(() =>
  staffSettingsStore.positionEmployees.filter(item => selectedEmployeeIds.value.includes(String(item.id))),
)

const transferPositions = computed(() =>
  (staffSettingsStore.allPositionsSimple || [])
    .filter(item => String(item.id) !== selectedId.value)
    .map(item => ({ id: String(item.id), label: item.name })),
)

watch(transferOpen, open => {
  if (open)
    staffSettingsStore.fetchAllPositionsSimple()
})

watch(listSearchDebounced, async value => {
  const seq = ++listSearchSeq
  listSearchLoading.value = true
  staffSettingsStore.positionsFilters.search = value ?? ''
  try {
    await staffSettingsStore.fetchPositionsForSidebar({ silent: true })
  } finally {
    if (seq === listSearchSeq)
      listSearchLoading.value = false
  }
})

onMounted(async () => {
  staffSettingsStore.positionsFilters.search = ''
  await staffSettingsStore.fetchPositionsForSidebar()
  const first = staffSettingsStore.positionsList[0]
  if (!selectedId.value && first)
    selectedId.value = String(first.id)
})

function setSort(ordering: string) {
  staffSettingsStore.currentPositionsSortString = ordering
  staffSettingsStore.fetchPositionsForSidebar()
}

function askDelete(id: string, name: string, count: number) {
  deleteId.value = id
  deleteName.value = name
  if (count > 0) {
    blockedCount.value = count
    blockedOpen.value = true
    return
  }
  deleteOpen.value = true
}

async function confirmDelete(ok: boolean) {
  deleteOpen.value = false
  if (!ok || !deleteId.value) return
  const removed = await staffSettingsStore.deletePosition(deleteId.value)
  if (removed && selectedId.value === deleteId.value)
    selectedId.value = null
  deleteId.value = null
}

async function assignToPosition(targetId: string, ids: string[]) {
  if (!ids.length) return false
  try {
    for (const id of ids)
      await staffStore.updateEmployee(id, { position: { id: targetId } })
    feedbackStore.showToast({
      type: 'success',
      message: 'Сотрудники назначены на должность',
      time: 4000,
    })
    if (selectedId.value)
      await staffSettingsStore.fetchPositionEmployees(selectedId.value)
    await staffSettingsStore.fetchPositionsForSidebar()
    selectedEmployeeIds.value = []
    return true
  } catch (error) {
    feedbackStore.showToast({
      type: 'error',
      message: getApiError(error, 'Не удалось назначить сотрудников'),
      time: 5000,
    })
    return false
  }
}

async function onDrop(targetId: string, event: DragEvent) {
  dragOverId.value = null
  const employeesRaw = event.dataTransfer?.getData('application/x-staff-employees')
  if (!employeesRaw) return
  const ids = JSON.parse(employeesRaw) as string[]
  await assignToPosition(targetId, ids)
}

async function fetchEmployees(
  search: string,
  cursor?: string,
  limit?: number,
  sort?: string | null,
  filters?: Record<string, any>,
) {
  if (!selectedId.value) return
  await staffSettingsStore.fetchPositionEmployees(selectedId.value, search, cursor, limit, sort, filters)
}

async function openAssign() {
  if (!apiStore.employees?.length)
    await apiStore.fetchAllEmployees()
  employeeModalRef.value?.open()
}

async function assignEmployees(selected: IEmployeeTreeItem[]) {
  if (!selectedId.value || !selected.length) return
  await assignToPosition(selectedId.value, selected.map(item => String(item.id)))
}

function removeTransferred(id: string) {
  selectedEmployeeIds.value = selectedEmployeeIds.value.filter(item => item !== id)
}

async function transferSelected(targetId: string) {
  if (!selectedEmployeeIds.value.length || transferring.value) return
  transferring.value = true
  const moved = await assignToPosition(targetId, [...selectedEmployeeIds.value])
  transferring.value = false
  if (moved)
    transferOpen.value = false
}

function openEdit(id: string) {
  editingPositionId.value = id
  editOpen.value = true
}
</script>

<template>
  <div class="d-flex flex-column ga-4">
    <StaffSettingsSplit>
      <template #header>
        <div class="d-flex align-center justify-space-between ga-4 flex-wrap pa-4">
          <span class="body-1-500 staff-dnd-hint">
            Вы можете включить возможность перемещать сотрудников на должность перетаскиванием.
          </span>
          <VSwitch
            v-model="dragEnabled"
            hide-details
            density="compact"
            color="primary"
            inset
          >
            <template #label>
              <span class="body-1-500 staff-dnd-hint">Перетаскивание</span>
            </template>
          </VSwitch>
        </div>
        <VDivider />
      </template>
      <template #left>
        <div class="d-flex flex-column ga-3">
          <VBtn block color="primary" prepend-icon="ri-add-line" @click="createOpen = true">
            Добавить должность
          </VBtn>
          <div class="d-flex align-center ga-2 align-self-stretch">
            <VTextField
              :model-value="listSearch"
              placeholder="Найти..."
              prepend-inner-icon="ri-search-line"
              variant="underlined"
              density="compact"
              hide-details
              clearable
              class="staff-settings-search search-underlined"
              @update:model-value="listSearch = $event ?? ''"
            />
            <VMenu>
              <template #activator="{ props: menuProps }">
                <VIconBtn
                  v-bind="menuProps"
                  icon="ri-sort-desc"
                  :icon-size="20"
                  variant="outlined"
                  color="secondary"
                  rounded="lg"
                />
              </template>
              <VList density="compact">
                <VListItem title="По названию (А–Я)" @click="setSort('name')" />
                <VListItem title="По названию (Я–А)" @click="setSort('-name')" />
                <VListItem title="По числу сотрудников" @click="setSort('-employee_count')" />
              </VList>
            </VMenu>
          </div>
          <div v-if="listSearchLoading" class="staff-list-loader d-flex align-center justify-center">
            <AnimatedLogoLoader />
          </div>
          <div
            v-else-if="(listSearchDebounced || '').trim() && !staffSettingsStore.positionsList.length"
            class="text-body-2 text-medium-emphasis text-center"
          >
            Ничего не найдено
          </div>
          <template v-else>
          <StaffSettingsEntityRow
            v-for="item in staffSettingsStore.positionsList"
            :key="item.id"
            class="align-self-stretch w-100"
            :name="item.name"
            :highlight="listSearch"
            :count="item.employee_count ?? 0"
            :selected="selectedId === String(item.id)"
            menu="edit"
            :draggable="false"
            :drop-enabled="dragEnabled"
            :drag-over="dragOverId === String(item.id)"
            @select="selectedId = String(item.id)"
            @action="openEdit(String(item.id))"
            @remove="askDelete(String(item.id), item.name, item.employee_count ?? 0)"
            @dragover="dragOverId = String(item.id)"
            @drop="onDrop(String(item.id), $event)"
          />
          </template>
        </div>
      </template>
      <template #right>
        <StaffSettingsEmployeePanel
          v-model:selected-ids="selectedEmployeeIds"
          :items="staffSettingsStore.positionEmployees"
          :total="staffSettingsStore.positionEmployeesCount"
          :next-cursor="staffSettingsStore.positionEmployeesNext"
          :headers="employeeHeaders"
          :fetch-page="fetchEmployees"
          :reload-key="selectedId"
          :drag-enabled="dragEnabled"
          move-label="Перевести"
          add-label="Назначить"
          empty-text="Выберите должность"
          storage-key="staff-settings-position-employees"
          :row-editable="canEditEmployees"
          @move="transferOpen = true"
          @add="openAssign"
          @edit="openEditEmployee"
        />
      </template>
    </StaffSettingsSplit>

    <PositionFormModal v-model="createOpen" mode="create" @saved="staffSettingsStore.fetchPositionsForSidebar()" />
    <PositionFormModal
      v-model="editOpen"
      mode="edit"
      :position-id="editingPositionId"
      @saved="staffSettingsStore.fetchPositionsForSidebar()"
    />
    <TransferEmployeesModal
      v-model="transferOpen"
      :employees="selectedEmployees"
      :positions="transferPositions"
      :loading="transferring"
      @confirm="transferSelected"
      @remove="removeTransferred"
      @remove-all="selectedEmployeeIds = []"
    />
    <EmployeeSelectionModal
      ref="employeeModalRef"
      :employees="apiStore.employees"
      multiple
      title="Назначить сотрудников"
      apply-label="Назначить"
      @select-multiple="assignEmployees"
    />
    <EditEmployeeDialog
      v-model="editEmployeeOpen"
      :employee-id="editEmployeeId"
    />
    <ConfirmDialog
      v-model:is-dialog-visible="deleteOpen"
      :confirmation-question="`Удалить должность «${deleteName}»?`"
      confirmation-text="Должность будет удалена. У должностей, для которых она была вышестоящей, эта связь пропадёт."
      confirm-btn="Удалить"
      reject-btn="Отменить"
      @confirm="confirmDelete"
    />
    <ConfirmDialog
      v-model:is-dialog-visible="blockedOpen"
      confirmation-question="Невозможно удалить должность"
      :confirmation-text="`На должности находятся ${blockedCount} сотрудников. Сначала переведите их на другую должность.`"
      confirm-btn="Ок"
      @confirm="blockedOpen = false"
    />
  </div>
</template>

<style scoped lang="scss">
.staff-dnd-hint {
  color: rgba(var(--v-theme-text-primary), var(--v-medium-emphasis-opacity));
}

.staff-settings-search {
  flex: 1 1 auto;
  min-inline-size: 0;
  max-inline-size: 100%;
}

.staff-list-loader {
  min-height: 160px;
}

:deep(.search-underlined.v-text-field) {
  .v-field__control {
    height: 36px !important;
  }

  .v-field__prepend-inner {
    align-self: center !important;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
  }

  .v-field__input {
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    min-height: 36px !important;
    align-self: center !important;
    display: flex !important;
    align-items: center !important;
  }
}
</style>
